import React from 'react';
import { SEOHead } from '../shared/components/SEOHead';
import './styles/privacy-policy.css';

export const PrivacyPolicyPage: React.FC = () => (
    <>
        <SEOHead seo={{
            title: 'Privacy Policy | Arte Craft India',
            description: 'Learn how Arte Craft India collects, uses, and protects your personal information when you shop with us.',
            keywords: 'privacy policy, data protection, personal information, Arte Craft India',
            canonical: `${window.location.origin}/privacy-policy`,
            ogType: 'website',
            noIndex: false,
        }} />

        <div className="pp-hero">
            <h1>Privacy Policy</h1>
            <p className="pp-updated">Last updated: May 2025</p>
        </div>

        <div className="pp-container">

            <p className="pp-intro">
                Arte Craft India ("we", "us", or "our") is committed to protecting your privacy.
                This Privacy Policy explains how we collect, use, disclose, and safeguard your
                information when you visit our website or place an order with us. Please read it
                carefully. If you do not agree with its terms, please stop using our site.
            </p>

            <div className="pp-section">
                <h2>1. Information We Collect</h2>
                <p>We collect information you provide directly to us, including:</p>
                <ul>
                    <li><strong>Account information:</strong> name, email address, phone number, and password when you register.</li>
                    <li><strong>Order information:</strong> billing address, shipping address, and order details when you make a purchase.</li>
                    <li><strong>Seller information:</strong> business name, GST number, bank details, and product listings when you register as a seller.</li>
                    <li><strong>Communications:</strong> messages you send us via contact forms, support tickets, or email.</li>
                </ul>
                <p>We also collect certain information automatically when you use our website, such as:</p>
                <ul>
                    <li>IP address, browser type, and operating system</li>
                    <li>Pages visited, time spent on pages, and referring URLs</li>
                    <li>Cookie and session identifiers</li>
                </ul>
            </div>

            <div className="pp-section">
                <h2>2. How We Use Your Information</h2>
                <p>We use the information we collect to:</p>
                <ul>
                    <li>Process and fulfill your orders, including sending order confirmations and shipping updates</li>
                    <li>Create and manage your account</li>
                    <li>Verify seller registrations and process approval workflows</li>
                    <li>Respond to your enquiries and provide customer support</li>
                    <li>Send transactional emails (order updates, password resets, email verification)</li>
                    <li>Send promotional communications where you have opted in (you may opt out at any time)</li>
                    <li>Detect, prevent, and investigate fraud, abuse, or security incidents</li>
                    <li>Improve our website and services through aggregate usage analytics</li>
                    <li>Comply with legal obligations</li>
                </ul>
            </div>

            <div className="pp-section">
                <h2>3. Information Sharing and Disclosure</h2>
                <p>
                    We do not sell, rent, or trade your personal information to third parties.
                    We may share your information in the following limited circumstances:
                </p>
                <ul>
                    <li><strong>Service providers:</strong> Shipping couriers and logistics partners receive your delivery address to fulfil orders. Payment processors receive billing information necessary to complete transactions.</li>
                    <li><strong>Platform sellers:</strong> When you purchase from a seller on our marketplace, the seller receives your name, delivery address, and order details to fulfil the order.</li>
                    <li><strong>Legal requirements:</strong> We may disclose information if required by law, court order, or to protect the rights and safety of Arte Craft India, our users, or others.</li>
                    <li><strong>Business transfers:</strong> If Arte Craft India is acquired or merges with another company, your information may be transferred as part of that transaction.</li>
                </ul>
            </div>

            <div className="pp-section">
                <h2>4. Data Security</h2>
                <p>
                    We implement industry-standard technical and organisational measures to protect your information:
                </p>
                <ul>
                    <li>Passwords are hashed using bcrypt with a high work factor — they are never stored in plain text.</li>
                    <li>Authentication uses short-lived JWT access tokens (15 minutes) with secure refresh token rotation.</li>
                    <li>All data in transit is protected with HTTPS/TLS encryption.</li>
                    <li>Login attempts are rate-limited and accounts are temporarily locked after repeated failed attempts.</li>
                    <li>HTTP security headers (Helmet.js), XSS filtering, and NoSQL injection sanitisation are applied server-side.</li>
                </ul>
                <p>
                    No method of transmission over the internet is 100% secure. While we strive to
                    protect your data, we cannot guarantee absolute security.
                </p>
            </div>

            <div className="pp-section">
                <h2>5. Cookies and Tracking</h2>
                <p>
                    We use essential cookies and local storage to maintain your session, remember your
                    shopping cart, and keep you logged in. We also use analytics to understand how
                    visitors use our site.
                </p>
                <p>
                    You can configure your browser to refuse cookies; however, some features of our
                    website may not function correctly without them.
                </p>
            </div>

            <div className="pp-section">
                <h2>6. Your Rights</h2>
                <p>You have the right to:</p>
                <ul>
                    <li><strong>Access:</strong> Request a copy of the personal information we hold about you.</li>
                    <li><strong>Correction:</strong> Ask us to correct inaccurate or incomplete information.</li>
                    <li><strong>Deletion:</strong> Request deletion of your account and associated personal data, subject to legal retention obligations.</li>
                    <li><strong>Opt-out:</strong> Unsubscribe from marketing emails at any time using the link in the email or by contacting us.</li>
                    <li><strong>Data portability:</strong> Request your data in a commonly used, machine-readable format.</li>
                </ul>
                <p>
                    To exercise any of these rights, please contact us at the address below. We will
                    respond within 30 days.
                </p>
            </div>

            <div className="pp-section">
                <h2>7. Third-Party Links</h2>
                <p>
                    Our website may contain links to third-party websites (such as social media platforms
                    or payment gateways). We are not responsible for the privacy practices of those sites
                    and encourage you to review their privacy policies before providing any information.
                </p>
            </div>

            <div className="pp-section">
                <h2>8. Children's Privacy</h2>
                <p>
                    Our services are not directed to individuals under the age of 13. We do not
                    knowingly collect personal information from children. If you believe a child has
                    provided us with their information, please contact us and we will delete it promptly.
                </p>
            </div>

            <div className="pp-section">
                <h2>9. Changes to This Policy</h2>
                <p>
                    We may update this Privacy Policy from time to time. When we do, we will revise the
                    "Last updated" date at the top of this page. We encourage you to review this policy
                    periodically to stay informed about how we protect your information. Continued use of
                    our website after any changes constitutes acceptance of the updated policy.
                </p>
            </div>

            <div className="pp-section">
                <h2>10. Contact Us</h2>
                <p>
                    If you have questions, concerns, or requests regarding this Privacy Policy or your
                    personal data, please reach out to us:
                </p>
                <div className="pp-contact-box">
                    <p><strong>Arte Craft India</strong></p>
                    <p>280/13 Gali No. 29, Molarband Extn, Badarpur, New Delhi – 110044</p>
                    <p>Email: <a href="mailto:info@artecraftindia.com">info@artecraftindia.com</a></p>
                    <p>Phone: <a href="tel:+919818548360">+91 9818548369</a></p>
                </div>
            </div>

        </div>
    </>
);
